/* eslint-disable react-hooks/exhaustive-deps */
import { EQ, GT, GTE, IS_NOT, LT, LTE, } from "../../../misc/RuleObjectTypes";
import React, { useCallback, useEffect, useState } from "react";
import ServiceWrapper from "../../../../../../Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import AppSettings from "Settings/AppSettings";
import Select from "react-select";
import debounce from "lodash/debounce";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import { LAYERS } from "Components/DesignSystem/layers";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Add, Minus } from "Components/DesignSystem/Iconography/Icons";

/* The old <option> list, verbatim — labels are what UPDATE_CONDITION_OP stores
   as conditionOpName, so they must not drift. */
const OPERATOR_OPTIONS = [
  { value: GT, label: ">" },
  { value: LT, label: "<" },
  { value: GTE, label: ">=" },
  { value: LTE, label: "<=" },
  { value: EQ, label: "==" },
  { value: IS_NOT, label: "!=" },
];

const FilterCondition = ({ element, position, reduceFilters, categoryAttributeSet, filterCount, }) => {
  
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  
  useEffect(() => {
    if (element?.attrID) {
      getAttributeValues(element?.attrID, page, searchValue);
    }
  }, [element.attrID, page, searchValue]);

  const getAttributeValues = async (attributeId, page, searchString = '') => {
    try {
      const res = await ServiceWrapper.doPost(
        `${api_base_url}get_attribute_values?page=${page}&limit=10&search=${searchString}`,
        {attribute_ids: [attributeId]},
        null,
        AuthManager.getGmsToken()
      );
      const attributeValues = res?.data?.data[0]?.attribute_values ?? [];
      handleAttributeResponse(attributeValues);
    } catch (error) {
      console.log("getAttributeValues ~ error:", error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleAttributeResponse = (response) => {
    const options = (response || []).map(item => ({ value: item.id, label: item.value }));
  
    if (page === 1 && element?.AttrValueId !== 0) {
      const isAttributeIdPresent = options.some(option => option.value === element.AttrValueId);
      const updatedOptions = isAttributeIdPresent
        ? options
        : [{ value: element.AttrValueId, label: element?.AttrValueName ?? '' }, ...options];

      setOptions(prevData => (page === 1 ? updatedOptions : [...prevData, ...updatedOptions]));
    } else {
      const filteredAttribute = options.filter(option => option.value !== element.AttrValueId);
      setOptions(prevData => (page === 1 ? filteredAttribute : [...prevData, ...filteredAttribute]));
    }

    setNoResults(options.length === 0);
  }

  const loadMoreOptions = async () => {
    if (!noResults) {
      setPage(prevPage => prevPage + 1);
    } else {
      setIsLoading(false)
    }
  };

  const debouncedLoadMoreOptions = useCallback(
    debounce(() => loadMoreOptions(), 500),
    [page]
  );

  const debouncedSearch = debounce((value) => {
    setSearchValue(value);
    setPage(1); 
  }, 500);

  const onInputChange = (input) => {
    if (input === '') {
      setPage(1);
    }
    debouncedSearch(input);
  };

  const handleMenuScrollToBottom = () => {
    setIsLoading(true);
    debouncedLoadMoreOptions();
  };

  /* DS Dropdown hands back the option VALUE; the old native select emitted
     ev.target.value (a STRING) and the option's text. Same payload, same types —
     the name comes from the list the value was chosen from. */
  const handleAttributeChange = (value) => {
    resetState();
    const chosen = (categoryAttributeSet || []).find((el) => String(el.id) === String(value));
    reduceFilters({ type: "UPDATE_ATTR", attrID: String(value), attrName: chosen?.name ?? "", position: position, });
  }

  const handleAttributeValueChange = (ev) => {
    reduceFilters({ type: "UPDATE_LOV_ID", AttrValueId: ev.value, AttrValueName: ev.label, position: position,});
  }

  const handleOperatorChange = (value) => {
    const chosen = OPERATOR_OPTIONS.find((op) => op.value === Number(value));
    reduceFilters({ type: "UPDATE_CONDITION_OP", position: position, conditionOpName: chosen?.label ?? "", conditionOperator: String(value), });
  }

  const resetState = () => {
    setOptions([]);
    setNoResults(false);
    setPage(1);
    setSearchValue('');
  }

  /* Dark pass (okf/rules, 2026-09-08): react-select paints its own white
     control/menu, which no data-theme can reach — every value below is a
     --ds-* var with the light value as fallback, so light is unchanged and
     dark follows the seam. Styling only; behaviour untouched. */
  const customStyles = {
    control: (provided) => ({
      ...provided,
      textAlign: 'left',
      backgroundColor: 'var(--ds-surface, #ffffff)',
      borderColor: 'var(--ds-edge-strong, #8891a0)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--ds-surface-raised, #ffffff)',
    }),
    option: (provided, state) => ({
      ...provided,
      textAlign: 'left',
      backgroundColor: state.isFocused ? 'var(--ds-surface-sunken, #f4f5f7)' : 'transparent',
      color: 'var(--ds-ink, #172b4d)',
    }),
    singleValue: (provided) => ({ ...provided, color: 'var(--ds-ink, #172b4d)' }),
    input: (provided) => ({ ...provided, color: 'var(--ds-ink, #172b4d)' }),
    placeholder: (provided) => ({ ...provided, color: 'var(--ds-ink-secondary, #505866)' }),
    /* The menu PORTALS to <body> (menuPortalTarget below) — inline it rendered
       inside the DS Dialog's overflow-y-auto content and slid UNDER the footer
       band (user screenshot, 2026-09-08): the same clip every DS Dropdown in
       this dialog avoids via usePortal, in react-select's dialect. The z is the
       § 204 ladder's top rung, the § 230 reasoning verbatim; the font restates
       Noto because a body-portalled surface inherits body's -apple-system-first
       stack (§ 195). */
    menuPortal: (provided) => ({
      ...provided,
      zIndex: LAYERS.tooltip,
      fontFamily: '"Noto Sans", sans-serif',
    }),
  };

  if (element) {
    return (
      <div className="filter-condition">
        <div className="filter-left-bracket" />
        <div className="filter-label">
          <Typography variant="body-primary" as="span" className="tw-text-ink">
            when
          </Typography>
        </div>
        {/* DS Dropdowns (okf/rules § 4.4) — `usePortal` because the rows live in
            the DS Dialog's overflow-y-auto content (the Guide-dialog clip).
            attrID/conditionOperator are 0 until chosen; 0 matches no option, so
            the placeholder carries the old hidden "Please Select".
            `searchable` (QA, 2026-10-05): a category can carry many attributes, so
            the list gets a filter box (`…-search`). Client-side: categoryAttributeSet
            is already loaded, so no onSearchChange; the query clears on close. */}
        <div className="attr-selector tw-text-sm tw-font-medium">
          <Dropdown
            label=""
            aria-label="Filter attribute"
            data-testid="rule-filter-attribute-select"
            usePortal
            searchable
            searchPlaceholder="Search attributes"
            placeholder="Please Select"
            options={(categoryAttributeSet || []).map((el) => ({ value: String(el.id), label: el.name }))}
            value={element.attrID === 0 ? undefined : String(element.attrID)}
            onChange={handleAttributeChange}
          />
        </div>
        <div className="op-selector val-selector">
          <Dropdown
            label=""
            aria-label="Filter condition operator"
            data-testid="rule-filter-condition-operator-select"
            usePortal
            placeholder="Please Select"
            options={OPERATOR_OPTIONS}
            value={element.conditionOperator === 0 ? undefined : Number(element.conditionOperator)}
            onChange={handleOperatorChange}
          />
        </div>
        <Select
          className="tw-outline-none"
          options={options} 
          styles={customStyles}
          onMenuScrollToBottom={handleMenuScrollToBottom}
          isLoading={isLoading}
          onInputChange={(input) => onInputChange(input)}
          value={element.AttrValueId ? {value: element.AttrValueId, label: element.AttrValueName} : null}
          onChange={(ev) => { handleAttributeValueChange(ev); }}
          placeholder={'Select Attribute'}
          maxMenuHeight={220}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          isClearable={false}
          components={{ IndicatorSeparator: () => null, }}
        />
        {/* DS IconButtons on the § 212 grammar: remove is the row delete
            (danger-subtle), add is the quiet glyph action (subtle) — replacing
            bare buttons around FontAwesome glyphs. Testids unchanged. */}
        <div className="filter-btn">
          {filterCount > 1 ? (
            <IconButton
              variant="danger-subtle"
              size="sm"
              data-testid="rule-filter-remove-condition-btn"
              aria-label="Remove filter condition"
              onClick={() => {
                reduceFilters({
                  type: "REMOVE_FILTER_CONDITION",
                  position: position,
                });
              }}
            >
              <Minus size={16} aria-hidden="true" />
            </IconButton>
          ) : null}
        </div>
        <div className="filter-right-bracket" />
        <div className="filter-add-btn filter-btn">
          <IconButton
            variant="subtle"
            size="sm"
            data-testid="filters-filtercondition-button-1"
            aria-label="Add filter condition"
            onClick={() => {
              reduceFilters({
                type: "ADD_FILTER_CONDITION",
                position: position,
              });
            }}
          >
            <Add size={16} aria-hidden="true" />
          </IconButton>
        </div>
      </div>
    );
  } else return null;
};

export default FilterCondition;